import axios from 'axios';
import { mediaApi, youtubeApi } from '@/api/endpoints';
import type { MediaAsset } from '@/types/api';
import type { YouTubeDirectUpload } from '@/types/youtube';
import {
  ALLOWED_VIDEO_MIME_TYPES,
  MAX_VIDEO_BYTES,
} from '@/utils/mediaValidation';
import { looksLikeShort } from '@/utils/youtube';

/**
 * Getting a video to YouTube from this browser.
 *
 * Two routes, decided per file:
 *
 * - **A Short** (square or vertical, three minutes or less) that fits the media
 *   library goes into the library, like any other post's video. The server
 *   sends it on to YouTube, so it can be scheduled, queued and retried
 *   without this page.
 * - **Everything else goes straight to YouTube** and is never stored by us.
 *   The server opens a resumable session with the channel's token; this
 *   browser sends the bytes into it; the server asks YouTube how far it got
 *   and which video it became. That means this page has to stay open while
 *   the bytes go up — the uploads below survive the dialog closing and moving
 *   around the app, not a reload. Choosing the same file again after one
 *   resumes where it stopped.
 */

export interface LocalVideoInfo {
  width: number | null;
  height: number | null;
  durationSeconds: number | null;
}

const NO_INFO: LocalVideoInfo = {
  width: null,
  height: null,
  durationSeconds: null,
};

/**
 * A local file's shape and length, as the browser reads them. Null parts for
 * a container the browser cannot open (MKV, AVI, ProRes) — those go straight
 * to YouTube, which can.
 */
export function probeLocalVideo(file: File): Promise<LocalVideoInfo> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    const finish = (info: LocalVideoInfo) => {
      window.clearTimeout(timer);
      video.removeAttribute('src');
      video.load();
      URL.revokeObjectURL(url);
      resolve(info);
    };
    const timer = window.setTimeout(() => finish(NO_INFO), 15_000);
    video.onloadedmetadata = () =>
      finish({
        width: video.videoWidth || null,
        height: video.videoHeight || null,
        durationSeconds: Number.isFinite(video.duration)
          ? video.duration
          : null,
      });
    video.onerror = () => finish(NO_INFO);
    video.src = url;
  });
}

export type UploadRoute = 'library' | 'youtube';

/** Shorts that fit the library are kept there; everything else goes to YouTube. */
export function uploadRoute(
  file: File,
  info: LocalVideoInfo,
  libraryMaxBytes = MAX_VIDEO_BYTES
): UploadRoute {
  const libraryType = ALLOWED_VIDEO_MIME_TYPES.includes(file.type);
  return libraryType &&
    file.size <= libraryMaxBytes &&
    looksLikeShort(info.width, info.height, info.durationSeconds)
    ? 'library'
    : 'youtube';
}

export interface UploadProgress {
  sentBytes: number;
  totalBytes: number;
  /** Bytes per second over the last few seconds. */
  rate: number;
}

/** Bytes per second from a stream of "sent so far" readings. */
function rateMeter() {
  const samples: { at: number; bytes: number }[] = [];
  return (bytes: number): number => {
    const now = Date.now();
    samples.push({ at: now, bytes });
    while (samples.length > 2 && now - samples[0].at > 5000) samples.shift();
    const first = samples[0];
    return now > first.at
      ? Math.max(0, ((bytes - first.bytes) * 1000) / (now - first.at))
      : 0;
  };
}

/** A Short into the media library, with progress. */
export async function uploadToLibrary(
  file: File,
  options: {
    onProgress?: (progress: UploadProgress) => void;
    signal?: AbortSignal;
  } = {}
): Promise<MediaAsset> {
  const rate = rateMeter();
  return mediaApi.upload(file, undefined, {
    signal: options.signal,
    onProgress: (loaded, total) =>
      options.onProgress?.({
        sentBytes: loaded,
        totalBytes: total,
        rate: rate(loaded),
      }),
  });
}

/* ─── Straight to YouTube ───────────────────────────────────────────────── */

export type DirectUploadPhase =
  | 'queued'
  | 'uploading'
  /** Every byte sent; the server is confirming the video with YouTube. */
  | 'finishing'
  | 'done'
  /** Stopped by a connection that would not come back. Retry carries on. */
  | 'paused'
  | 'failed'
  | 'cancelled';

export interface DirectUploadEntry {
  uploadId: string;
  accountId: string;
  fileName: string;
  totalBytes: number;
  sentBytes: number;
  rate: number;
  phase: DirectUploadPhase;
  videoId: string | null;
  error: string | null;
}

/** Two at a time: enough to fill most connections, no more. */
const PARALLEL = 2;
const RETRIES = 6;
/** How often the server records progress while a file goes up. */
const CHECK_EVERY_MS = 30_000;
const DEFAULT_CHUNK_BYTES = 32 * 1024 * 1024;
const RESUME_KEY = 'zaag.youtube.direct.v1';

const entries = new Map<string, DirectUploadEntry>();
const files = new Map<string, File>();
const controllers = new Map<string, AbortController>();
const listeners = new Set<() => void>();
let snapshot: DirectUploadEntry[] = [];

export function subscribeDirectUploads(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function directUploadsSnapshot(): DirectUploadEntry[] {
  return snapshot;
}

export function directUpload(uploadId: string): DirectUploadEntry | null {
  return entries.get(uploadId) ?? null;
}

function emit() {
  snapshot = [...entries.values()];
  listeners.forEach((listener) => listener());
  guardUnload();
}

function patch(uploadId: string, next: Partial<DirectUploadEntry>) {
  const entry = entries.get(uploadId);
  if (!entry) return;
  entries.set(uploadId, { ...entry, ...next });
  emit();
}

const isActive = (entry: DirectUploadEntry) =>
  entry.phase === 'queued' ||
  entry.phase === 'uploading' ||
  entry.phase === 'finishing';

// Leaving the page stops the bytes; the browser asks first.
let guarding = false;
function onBeforeUnload(event: BeforeUnloadEvent) {
  event.preventDefault();
  event.returnValue = '';
}
function guardUnload() {
  const active = [...entries.values()].some(isActive);
  if (active && !guarding) {
    window.addEventListener('beforeunload', onBeforeUnload);
    guarding = true;
  } else if (!active && guarding) {
    window.removeEventListener('beforeunload', onBeforeUnload);
    guarding = false;
  }
}

// The upload id per file, so choosing the same file again resumes it.
function fileKey(accountId: string, file: File): string {
  return `${accountId}|${file.name}|${file.size}|${file.lastModified}`;
}
function readResume(): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem(RESUME_KEY) ?? '{}');
  } catch {
    return {};
  }
}
function writeResume(map: Record<string, string>) {
  try {
    localStorage.setItem(RESUME_KEY, JSON.stringify(map));
  } catch {
    // Private mode or a full quota: resuming is a nicety.
  }
}
function remember(key: string, uploadId: string) {
  writeResume({ ...readResume(), [key]: uploadId });
}
function forget(uploadId: string) {
  const map = readResume();
  for (const [key, id] of Object.entries(map)) {
    if (id === uploadId) delete map[key];
  }
  writeResume(map);
}

let running = 0;
const waiting: (() => void)[] = [];
async function acquire(): Promise<void> {
  if (running < PARALLEL) {
    running += 1;
    return;
  }
  await new Promise<void>((resolve) => waiting.push(resolve));
  running += 1;
}
function release() {
  running -= 1;
  waiting.shift()?.();
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
const backoff = (failures: number) => Math.min(30_000, 1000 * 2 ** failures);

export class DirectUploadMismatchError extends Error {}

/**
 * Open an upload for a file and start sending it.
 *
 * Resolves as soon as YouTube has a session — the upload id is what a post
 * points to — while the bytes carry on in the background. An open upload of
 * this exact file is resumed rather than started again.
 */
export async function startDirectUpload(
  file: File,
  input: { accountId: string; info: LocalVideoInfo; title?: string }
): Promise<YouTubeDirectUpload> {
  const key = fileKey(input.accountId, file);
  const remembered = readResume()[key];
  if (remembered) {
    const running = entries.get(remembered);
    if (running && isActive(running)) {
      return youtubeApi.checkDirectUpload(remembered);
    }
    try {
      const view = await youtubeApi.checkDirectUpload(remembered);
      if (
        view.status === 'UPLOADING' &&
        view.sessionUrl &&
        view.totalBytes === file.size
      ) {
        track(view, file, input.accountId);
        return view;
      }
    } catch {
      // Gone, or not ours any more: start afresh.
    }
    forget(remembered);
  }

  const view = await youtubeApi.startDirectUpload(input.accountId, {
    fileName: file.name,
    sizeBytes: file.size,
    mimeType: file.type || undefined,
    width: input.info.width ?? undefined,
    height: input.info.height ?? undefined,
    durationSeconds: input.info.durationSeconds ?? undefined,
    title: input.title,
  });
  remember(key, view.id);
  track(view, file, input.accountId);
  return view;
}

/**
 * Carry on with an upload after a reload, from the same file chosen again.
 * Refuses a different file: the session only takes the bytes it was opened for.
 */
export async function resumeDirectUpload(
  file: File,
  uploadId: string,
  accountId: string
): Promise<YouTubeDirectUpload> {
  const view = await youtubeApi.checkDirectUpload(uploadId);
  if (view.status !== 'UPLOADING' || !view.sessionUrl) return view;
  if (view.totalBytes !== file.size) {
    throw new DirectUploadMismatchError(
      'That is not the file this upload started with — its size is different.'
    );
  }
  const running = entries.get(uploadId);
  if (running && isActive(running)) return view;
  remember(fileKey(accountId, file), uploadId);
  track(view, file, accountId);
  return view;
}

/** Try a paused upload again, with the file this page still holds. */
export function retryDirectUpload(uploadId: string): boolean {
  const entry = entries.get(uploadId);
  const file = files.get(uploadId);
  if (!entry || !file || entry.phase !== 'paused') return false;
  patch(uploadId, { phase: 'queued', error: null });
  void youtubeApi
    .checkDirectUpload(uploadId)
    .then((view) => {
      if (view.status === 'UPLOADING' && view.sessionUrl) {
        void run(view, file);
      } else {
        settle(uploadId, view);
      }
    })
    .catch((error) =>
      patch(uploadId, {
        phase: 'paused',
        error: error?.message ?? 'Could not reach the server.',
      })
    );
  return true;
}

/**
 * Stop an upload. An unfinished one is abandoned (YouTube discards it); a
 * finished one is left on the channel as Private unless `deleteVideo`.
 */
export async function cancelDirectUpload(
  uploadId: string,
  deleteVideo = false
): Promise<void> {
  controllers.get(uploadId)?.abort();
  patch(uploadId, { phase: 'cancelled' });
  forget(uploadId);
  await youtubeApi.cancelDirectUpload(uploadId, deleteVideo);
}

/** Take a finished or stopped upload off the list. */
export function dismissDirectUpload(uploadId: string) {
  const entry = entries.get(uploadId);
  if (!entry || isActive(entry)) return;
  entries.delete(uploadId);
  files.delete(uploadId);
  emit();
}

function track(view: YouTubeDirectUpload, file: File, accountId: string) {
  entries.set(view.id, {
    uploadId: view.id,
    accountId,
    fileName: file.name,
    totalBytes: file.size,
    sentBytes: view.nextByte ?? 0,
    rate: 0,
    phase: 'queued',
    videoId: null,
    error: null,
  });
  files.set(view.id, file);
  emit();
  void run(view, file);
}

/** A server view that means the upload is over, one way or another. */
function settle(uploadId: string, view: YouTubeDirectUpload) {
  if (view.status === 'UPLOADED') {
    forget(uploadId);
    patch(uploadId, {
      phase: 'done',
      videoId: view.videoId,
      sentBytes: view.totalBytes,
      rate: 0,
      error: null,
    });
  } else {
    forget(uploadId);
    patch(uploadId, {
      phase: view.status === 'CANCELLED' ? 'cancelled' : 'failed',
      error: view.error ?? null,
      rate: 0,
    });
  }
}

async function check(uploadId: string): Promise<YouTubeDirectUpload | null> {
  try {
    return await youtubeApi.checkDirectUpload(uploadId);
  } catch {
    return null;
  }
}

/**
 * Send a file into its session, chunk by chunk, in order.
 *
 * YouTube's own answer to a chunk says how much it kept. When that answer is
 * missing or unreadable — a dropped connection, a response the browser will
 * not show — the server asks YouTube instead, and the upload carries on from
 * whatever YouTube actually holds. The browser's own count is never trusted
 * for where to resume.
 */
async function run(start: YouTubeDirectUpload, file: File): Promise<void> {
  const id = start.id;
  const controller = new AbortController();
  controllers.set(id, controller);
  await acquire();
  try {
    if (controller.signal.aborted) return;
    patch(id, { phase: 'uploading' });

    const session = start.sessionUrl!;
    const chunk = start.chunkBytes ?? DEFAULT_CHUNK_BYTES;
    const total = file.size;
    const rate = rateMeter();
    let offset = start.nextByte ?? 0;
    let failures = 0;
    let lastCheck = Date.now();

    const show = (sent: number) =>
      patch(id, { sentBytes: Math.min(sent, total), rate: rate(sent) });

    for (;;) {
      while (offset < total) {
        if (controller.signal.aborted) return;
        const end = Math.min(offset + chunk, total);

        let answer: 'complete' | number | null = null;
        try {
          const response = await axios.put(session, file.slice(offset, end), {
            signal: controller.signal,
            headers: { 'Content-Range': `bytes ${offset}-${end - 1}/${total}` },
            // A Blob as it is: no JSON, no auth header — this goes to YouTube.
            transformRequest: [(data) => data],
            validateStatus: (status) =>
              status === 308 || (status >= 200 && status < 300),
            onUploadProgress: (event) => show(offset + (event.loaded ?? 0)),
          });
          if (response.status !== 308) {
            answer = 'complete';
          } else {
            const range = String(response.headers?.range ?? '');
            const match = /bytes=\d+-(\d+)/.exec(range);
            answer = match ? Number(match[1]) + 1 : null;
          }
        } catch (error) {
          if (controller.signal.aborted || axios.isCancel(error)) return;
          answer = null;
        }

        if (answer === 'complete') {
          offset = total;
          break;
        }
        if (typeof answer === 'number' && answer > offset) {
          offset = answer;
          failures = 0;
          show(offset);
        } else {
          // Unreadable or failed: what does YouTube really have?
          if (failures) await sleep(backoff(failures));
          const view = await check(id);
          if (view && view.status !== 'UPLOADING') {
            settle(id, view);
            return;
          }
          const next = view?.nextByte ?? offset;
          failures = next > offset ? 0 : failures + 1;
          if (failures >= RETRIES) {
            patch(id, {
              phase: 'paused',
              rate: 0,
              error:
                'The connection to YouTube keeps dropping. Retry to carry on from where it stopped.',
            });
            return;
          }
          offset = next;
          lastCheck = Date.now();
          show(offset);
          continue;
        }

        if (Date.now() - lastCheck > CHECK_EVERY_MS) {
          lastCheck = Date.now();
          const view = await check(id);
          if (view && view.status !== 'UPLOADING') {
            settle(id, view);
            return;
          }
        }
      }

      // Every byte sent. The server confirms it with YouTube and learns the video id.
      patch(id, { phase: 'finishing', sentBytes: total, rate: 0 });
      let view: YouTubeDirectUpload | null = null;
      for (let attempt = 0; attempt < RETRIES; attempt += 1) {
        view = await check(id);
        if (view) break;
        await sleep(backoff(attempt + 1));
      }
      if (!view) {
        patch(id, {
          phase: 'paused',
          error:
            'Could not confirm the upload with YouTube. Retry to check again.',
        });
        return;
      }
      if (view.status !== 'UPLOADING') {
        settle(id, view);
        return;
      }
      // YouTube is missing some of it after all: send the rest.
      offset = view.nextByte ?? 0;
      patch(id, { phase: 'uploading' });
    }
  } finally {
    release();
    controllers.delete(id);
  }
}
